import React, { useEffect, useState } from "react";
import "./Devices.styles.scss";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SearchTable } from "../../../../components/SearchTable";
import * as api from "../../../../api/methods";
import { COLUMNS } from "./Devices.config";
import { USERNAME } from "../../../../constants/common";
import { Modal } from "../Modal";
import {showToast} from "../../../../helpers/showToast"; // Your enhanced Modal component

const Devices = ({ items, onSearch, taxNumber, name }) => {
  const [renderItem, setRenderItems] = useState([]);
  const [loading, setLoading] = useState({});
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCustomerPriceNoteModal, setShowCustomerPriceNoteModal] = useState(false);
  const [showSummaryInvoiceModal, setShowSummaryInvoiceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const { t } = useTranslation();

  useEffect(() => {
    setRenderItems(items);
  }, [items]);

  const openNoteModal = (rowData) => {
    setSelectedDevice({ ...rowData, taxNumber });
    setShowNoteModal(true);
  };

  const openCustomerPriceNoteModal = (rowData) => {
    setSelectedDevice({ ...rowData, taxNumber });
    setShowCustomerPriceNoteModal(true);
  };

  const openSummaryInvoiceModal = (rowData) => {
    setSelectedDevice({ ...rowData, taxNumber });
    setShowSummaryInvoiceModal(true);
  };

  // Memoize the custom inputs to prevent re-renders
  const summaryInvoiceCustomInputs = React.useMemo(() => [
    {
      key: "note",
      label: "Reason for change",
      type: "textarea",
      placeholder: "Please provide a reason for this change (minimum 4 characters)...",
      required: true,
      minLength: 4,
      rows: 3,
      initialValue: ""
    },
    {
      key: "transactionNumber",
      label: "Transaction Number",
      type: "text",
      placeholder: "Enter transaction number (e.g., SDC-224)...",
      required: true,
      minLength: 3,
      pattern: /^[A-Z0-9-]+$/i,
      patternError: "Transaction number can only contain letters, numbers, and hyphens",
      initialValue: ""
    }
  ], []);

  const closeNoteModal = () => {
    setShowNoteModal(false);
    setSelectedDevice(null);
  };

  const closeCustomerPriceNoteModal = () => {
    setShowCustomerPriceNoteModal(false);
    setSelectedDevice(null);
  };

  const closeSummaryInvoiceModal = () => {
    setShowSummaryInvoiceModal(false);
    setSelectedDevice(null);
  };

  const onDeviceStatusChange = (e, rowData) => {
    if(rowData.hasAnActiveCert) {
      showToast({
        toastOptions: {type: "error"},
        data: {
          text: "You can't disable this device because it has an active certificate."
        }
      });
    } else {
      if (e.value === true) {
        return;
      }
      openNoteModal(rowData);
    }
  };

  const onCustomerPriceStatusChange = (e, rowData) => {
    openCustomerPriceNoteModal(rowData);
  };

  const onSummayInvoiceStatusChange = (e, rowData) => {
    openSummaryInvoiceModal(rowData);
  };

  const handleSubmitNote = (note) => {
    // Close the modal
    closeNoteModal();

    // Proceed with your API call
    setLoading((prevState) => ({
      ...prevState,
      [selectedDevice.activityNumber]: true,
    }));

    const dataObject = {
      enabled: !selectedDevice.active,
      username: localStorage.getItem(USERNAME),
      tin: selectedDevice.taxNumber,
      note: note,
    };

    api
      .enableDisableDevice(selectedDevice.activityNumber, dataObject)
      .then(() => {
        const updatedItems = renderItem.map((item) => {
          if (item.activityNumber === selectedDevice.activityNumber) {
            item.active = !selectedDevice.active;
          }
          return item;
        });

        setLoading((prevState) => ({
          ...prevState,
          [selectedDevice.activityNumber]: false,
        }));

        setRenderItems(updatedItems);
      })
      .catch(() => {
        // Handle errors here
        setLoading((prevState) => ({
          ...prevState,
          [selectedDevice.activityNumber]: false,
        }));
      });
  };

  const handleSubmitCustomerPriceNote = (note) => {
    // Close the modal
    closeCustomerPriceNoteModal();

    // Proceed with your API call
    setLoading((prevState) => ({
      ...prevState,
      [selectedDevice.activityNumber]: true,
    }));

    const dataObject = {
      enabled: !selectedDevice.customerPriceEnabled,
      username: localStorage.getItem(USERNAME),
      tin: selectedDevice.taxNumber,
      note: note,
    };

    api
      .enableDisableCustomerPrice(selectedDevice.activityNumber, dataObject)
      .then(() => {
        const updatedItems = renderItem.map((item) => {
          if (item.activityNumber === selectedDevice.activityNumber) {
            item.customerPriceEnabled = !selectedDevice.customerPriceEnabled;
          }
          return item;
        });

        setLoading((prevState) => ({
          ...prevState,
          [selectedDevice.activityNumber]: false,
        }));

        setRenderItems(updatedItems);
      })
      .catch(() => {
        // Handle errors here
        setLoading((prevState) => ({
          ...prevState,
          [selectedDevice.activityNumber]: false,
        }));
      });
  };

  const handleSubmitSummaryInvoice = (inputs) => {
    // Close the modal
    closeSummaryInvoiceModal();

    // Proceed with your API call
    setLoading((prevState) => ({
      ...prevState,
      [selectedDevice.activityNumber]: true,
    }));

    const dataObject = {
      enabled: !selectedDevice.summaryInvoiceEnabled,
      username: localStorage.getItem(USERNAME),
      tin: selectedDevice.taxNumber,
      note: inputs.note,
      transactionNumber: inputs.transactionNumber,
    };

    // Replace this with your actual API call for summary invoice
    api
      .enableDisableSummaryInvoice(selectedDevice.activityNumber, dataObject)
      .then(() => {
        const updatedItems = renderItem.map((item) => {
          if (item.activityNumber === selectedDevice.activityNumber) {
            item.summaryInvoiceEnabled = !selectedDevice.summaryInvoiceEnabled;
          }
          return item;
        });

        setLoading((prevState) => ({
          ...prevState,
          [selectedDevice.activityNumber]: false,
        }));

        setRenderItems(updatedItems);
      })
      .catch(() => {
        // Handle errors here
        setLoading((prevState) => ({
          ...prevState,
          [selectedDevice.activityNumber]: false,
        }));
      });
  };

  return (
    <div className={"devices_container"}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <SearchTable
          placeholder={t("admin_control.searchPlaceholder")}
          onSearchHandler={onSearch}
        />
      </div>
      <DataTable
        value={renderItem}
        responsiveLayout="scroll"
        rowGroupMode="rowspan"
        groupRowsBy={["taxNumber", "name"]}
        emptyMessage=" "
      >
        {COLUMNS({
          loading,
          t,
          onDeviceStatusChange,
          onCustomerPriceStatusChange,
          onSummayInvoiceStatusChange,
          taxNumber,
          name,
        }).map((column) => {
          return (
            <Column
              field={column.field}
              header={column.header}
              sortable={column.sortable}
              body={column.body}
              key={column.field}
            />
          );
        })}
      </DataTable>

      {/* Device Status Modal */}
      <Modal
        isOpen={showNoteModal}
        onClose={closeNoteModal}
        onConfirm={handleSubmitNote}
        title="Device Status Change"
        content={
          <p>
            Are you sure you want to {selectedDevice?.active ? "disable" : "enable"} this activity?
            <br />
            <strong>Activity:</strong> {selectedDevice?.activityNumber}
          </p>
        }
        requireNote={true}
        noteLabel="Reason for change"
        notePlaceholder="Please provide a reason for this change (minimum 4 characters)..."
        initialNote=""
      />

      {/* Customer Price Modal */}
      <Modal
        isOpen={showCustomerPriceNoteModal}
        onClose={closeCustomerPriceNoteModal}
        onConfirm={handleSubmitCustomerPriceNote}
        title="Customer Price Setting"
        content={
          <p>
            Are you sure you want to {selectedDevice?.customerPriceEnabled ? "disable" : "enable"} customer pricing for this activity?
            <br />
            <strong>Activity:</strong> {selectedDevice?.activityNumber}
          </p>
        }
        requireNote={true}
        noteLabel="Reason for change"
        notePlaceholder="Please provide a reason for this change (minimum 4 characters)..."
        initialNote=""
      />

      {/* Summary Invoice Modal */}
      <Modal
        isOpen={showSummaryInvoiceModal}
        onClose={closeSummaryInvoiceModal}
        onConfirm={handleSubmitSummaryInvoice}
        title="Summary Invoice Setting"
        content={
          <p>
            Are you sure you want to {selectedDevice?.summaryInvoiceEnabled ? "disable" : "enable"} summary invoice for this activity?
            <br />
            <strong>Activity:</strong> {selectedDevice?.activityNumber}
          </p>
        }
        showNoteInput={false} // Disable the default note input
        customInputs={summaryInvoiceCustomInputs}
      />
    </div>
  );
};

export default React.memo(Devices);
